import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, X, AlertTriangle, Info, CheckCircle2, Volume2, VolumeX, Package, Tag, History } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from './ui/dialog';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription
} from './ui/drawer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Função de navegação segura (funciona em iframe e fora)
const navigateTo = (path) => {
  const baseUrl = window.location.origin;
  if (window.top !== window.self) {
    // Dentro de iframe - navegar na janela principal
    window.top.location.href = `${baseUrl}${path}`;
  } else {
    window.location.href = `${baseUrl}${path}`;
  }
};

// Função para tocar som de notificação usando Web Audio API
const playNotificationSound = () => {
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    // Som "ding" suave - frequência 800Hz, tipo sine
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    // Volume e fade out
    gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
    
    oscillator.start(audioCtx.currentTime);
    oscillator.stop(audioCtx.currentTime + 0.3);
    
    // Segundo tom para criar efeito "ding-ding"
    setTimeout(() => {
      const audioCtx2 = new (window.AudioContext || window.webkitAudioContext)();
      const osc2 = audioCtx2.createOscillator();
      const gain2 = audioCtx2.createGain();
      
      osc2.connect(gain2);
      gain2.connect(audioCtx2.destination);
      
      osc2.frequency.value = 1000; // Tom ligeiramente mais alto
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0.25, audioCtx2.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.01, audioCtx2.currentTime + 0.25);
      
      osc2.start(audioCtx2.currentTime);
      osc2.stop(audioCtx2.currentTime + 0.25);
    }, 150);
  } catch (e) {
    console.log('Erro ao tocar som:', e);
  }
};

const PRIORIDADE_CONFIG = {
  alta: { 
    icon: AlertTriangle, 
    bgColor: 'bg-red-50', 
    borderColor: 'border-red-200',
    textColor: 'text-red-700',
    iconColor: 'text-red-500'
  },
  normal: { 
    icon: Info, 
    bgColor: 'bg-blue-50', 
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    iconColor: 'text-blue-500'
  },
  baixa: { 
    icon: CheckCircle2, 
    bgColor: 'bg-green-50', 
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    iconColor: 'text-green-500'
  }
};

export default function AlertasNotificacao({ userId, perfil = 'operador', renderButton }) {
  const [alertas, setAlertas] = useState([]);
  const [alertasNaoLidos, setAlertasNaoLidos] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [alertaSelecionado, setAlertaSelecionado] = useState(null);
  const [showHistorico, setShowHistorico] = useState(false);
  const [todosAlertas, setTodosAlertas] = useState([]); // Inclui lidos e não lidos
  
  // Estado do som - carrega preferência do localStorage
  const [somAtivado, setSomAtivado] = useState(() => {
    const saved = localStorage.getItem('alertas_som_ativado');
    return saved !== null ? JSON.parse(saved) : true; // Ativado por defeito
  });
  
  // Ref para guardar o último count de alertas (para detetar novos)
  const ultimoCountRef = useRef(0);
  const primeiraCarregaRef = useRef(true);

  // Guardar preferência de som no localStorage
  useEffect(() => {
    localStorage.setItem('alertas_som_ativado', JSON.stringify(somAtivado));
  }, [somAtivado]);

  // Carregar alertas
  const carregarAlertas = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alertas/nao-lidos/${userId}`);
      const novosAlertas = response.data || [];
      const novoCount = novosAlertas.length;
      
      // Tocar som se há mais alertas do que antes (novos alertas chegaram)
      // Não tocar na primeira carga para evitar som ao abrir a página
      if (!primeiraCarregaRef.current && somAtivado && novoCount > ultimoCountRef.current) {
        playNotificationSound();
        // Auto-abrir o dialog com o alerta mais recente
        if (novosAlertas.length > 0) {
          setAlertaSelecionado(novosAlertas[0]);
          setShowDialog(true);
        }
      }
      
      primeiraCarregaRef.current = false;
      ultimoCountRef.current = novoCount;
      
      setAlertas(novosAlertas);
      setAlertasNaoLidos(novoCount);
    } catch (error) {
      console.log('Erro ao carregar alertas:', error);
      setAlertas([]);
    }
  }, [userId, somAtivado]);

  // Marcar alerta como lido
  const marcarComoLido = async (alertaId) => {
    try {
      await axios.post(`${API}/alertas/${alertaId}/marcar-lido`, { user_id: userId });
      carregarAlertas();
      carregarTodosAlertas();
    } catch (error) {
      console.log('Erro ao marcar alerta como lido:', error);
    }
  };

  // Carregar todos os alertas (lidos e não lidos) para o histórico
  const carregarTodosAlertas = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alertas/todos/${userId}`);
      setTodosAlertas(response.data || []);
    } catch (error) {
      console.log('Erro ao carregar histórico de alertas:', error);
      setTodosAlertas([]);
    }
  }, [userId]);

  // Carregar alertas ao montar e periodicamente
  useEffect(() => {
    if (userId) {
      carregarAlertas();
      carregarTodosAlertas();
      const interval = setInterval(carregarAlertas, 30000); // Atualizar a cada 30 segundos
      return () => clearInterval(interval);
    }
  }, [userId, carregarAlertas, carregarTodosAlertas]);

  // Listener para abrir drawer via evento customizado (do botão no header)
  useEffect(() => {
    const handleOpenDrawer = () => {
      carregarTodosAlertas();
      setShowHistorico(true);
    };
    window.addEventListener('openAlertasDrawer', handleOpenDrawer);
    return () => window.removeEventListener('openAlertasDrawer', handleOpenDrawer);
  }, [carregarTodosAlertas]);

  // Mostrar diálogo com alerta
  const handleVerAlerta = (alerta) => {
    setAlertaSelecionado(alerta);
    setShowDialog(true);
  };

  // Fechar e marcar como lido
  const handleFecharAlerta = () => {
    if (alertaSelecionado) {
      marcarComoLido(alertaSelecionado.id);
    }
    setShowDialog(false);
    setAlertaSelecionado(null);
  };

  const config = alertaSelecionado 
    ? PRIORIDADE_CONFIG[alertaSelecionado.prioridade] || PRIORIDADE_CONFIG.normal
    : PRIORIDADE_CONFIG.normal;

  // Função para abrir histórico (pode ser chamada externamente via renderButton)
  const abrirHistorico = () => {
    carregarTodosAlertas();
    setShowHistorico(true);
  };
  
  // Função para ver alerta (pode ser chamada externamente via renderButton)
  const verPrimeiroAlerta = () => {
    if (alertasNaoLidos > 0 && alertas.length > 0) {
      handleVerAlerta(alertas[0]);
    } else {
      abrirHistorico();
    }
  };

  return (
    <>
      {/* Renderizar botão customizado se fornecido */}
      {renderButton && renderButton({ 
        alertasNaoLidos, 
        onClick: verPrimeiroAlerta,
        onHistoricoClick: abrirHistorico 
      })}

      {/* Ícones de Som e Histórico - MOVIDOS para o header do OperadorHome */}
      {/* Esta div fica vazia, os ícones foram movidos */}

      {/* Dialog de Alerta */}
      <Dialog open={showDialog} onOpenChange={(open) => !open && handleFecharAlerta()}>
        <DialogContent className="max-w-md mx-auto rounded-2xl border-0 shadow-2xl">
          <DialogHeader className="sr-only">
            <DialogTitle>{alertaSelecionado?.titulo || 'Alerta'}</DialogTitle>
          </DialogHeader>
          
          {alertaSelecionado && (
            <div className="space-y-4">
              {/* Header com ícone */}
              <div className={`flex items-center gap-3 p-4 rounded-xl ${config.bgColor} ${config.borderColor} border`}>
                <div className={`p-2 rounded-full bg-white/80`}>
                  {React.createElement(config.icon, { className: `h-6 w-6 ${config.iconColor}` })}
                </div>
                <div>
                  <h3 className={`font-bold ${config.textColor}`}>
                    {alertaSelecionado.titulo}
                  </h3>
                  <p className="text-xs text-stone-500">
                    {alertaSelecionado.prioridade === 'alta' ? 'Prioridade Alta' : 
                     alertaSelecionado.prioridade === 'baixa' ? 'Informativo' : 'Aviso'}
                  </p>
                </div>
              </div>

              {/* Mensagem com mais destaque */}
              <div className="px-2 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-stone-800 leading-relaxed font-medium text-base">
                  {alertaSelecionado.mensagem}
                </p>
              </div>

              {/* Detalhes da OF (se disponível) */}
              {alertaSelecionado.dados_of && (
                <div className="mx-2 bg-stone-50 rounded-xl p-4 space-y-3 border border-stone-200">
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Detalhes da Tarefa</p>
                  
                  {/* OF e Marca */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-navy" />
                      <div>
                        <p className="text-xs text-stone-500">Ordem de Fabrico</p>
                        <p className="font-bold text-navy">{alertaSelecionado.dados_of.of_numero}</p>
                      </div>
                    </div>
                    {alertaSelecionado.dados_of.marca && (
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-sage" />
                        <div>
                          <p className="text-xs text-stone-500">Marca</p>
                          <p className="font-semibold text-stone-700">{alertaSelecionado.dados_of.marca}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Lote */}
                  {alertaSelecionado.dados_of.lote_codigo && (
                    <div className="flex items-center gap-2 bg-amber-50 rounded-lg px-3 py-2">
                      <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center">
                        <span className="text-xs font-bold text-amber-800">L</span>
                      </div>
                      <div>
                        <p className="text-xs text-amber-600">Lote</p>
                        <p className="font-bold text-amber-800">
                          {alertaSelecionado.dados_of.lote_codigo}
                          {alertaSelecionado.dados_of.lote_quantidade && (
                            <span className="font-normal text-amber-600"> ({alertaSelecionado.dados_of.lote_quantidade} un.)</span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Operação */}
                  {alertaSelecionado.dados_of.operacao_nome && (
                    <div className="bg-white rounded-lg px-3 py-2 border border-stone-200">
                      <p className="text-xs text-stone-500">Operação</p>
                      <p className="font-semibold text-stone-800">
                        {alertaSelecionado.dados_of.operacao_codigo && (
                          <span className="text-sage">{alertaSelecionado.dados_of.operacao_codigo} - </span>
                        )}
                        {alertaSelecionado.dados_of.operacao_nome}
                      </p>
                      {alertaSelecionado.dados_of.operacao_descricao && 
                       alertaSelecionado.dados_of.operacao_descricao !== alertaSelecionado.dados_of.operacao_nome && (
                        <p className="text-xs text-stone-500 mt-1">{alertaSelecionado.dados_of.operacao_descricao}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Data de notificação */}
              {alertaSelecionado.data_notificacao && (
                <div className="px-2 text-sm text-stone-500">
                  Data: {new Date(alertaSelecionado.data_notificacao).toLocaleString('pt-PT', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              )}

              {/* Botões */}
              <div className="flex flex-col gap-3 pt-2">
                <div className="flex gap-3">
                  {alertas.length > 1 && (
                    <Button
                      variant="outline"
                      className="flex-1 h-12 rounded-full"
                      onClick={() => {
                        marcarComoLido(alertaSelecionado.id);
                        const nextAlerta = alertas.find(a => a.id !== alertaSelecionado.id);
                        if (nextAlerta) {
                          setAlertaSelecionado(nextAlerta);
                        } else {
                          setShowDialog(false);
                        }
                      }}
                    >
                      Próximo ({alertas.length - 1})
                    </Button>
                  )}
                  <Button
                    className={`flex-1 h-12 rounded-full ${
                      alertaSelecionado.prioridade === 'alta' 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-navy hover:bg-navy-hover'
                    } text-white font-semibold`}
                    onClick={handleFecharAlerta}
                  >
                    Entendido
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Drawer de Histórico de Alertas */}
      <Drawer open={showHistorico} onOpenChange={setShowHistorico}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="border-b border-stone-100 pb-4">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-xl font-heading text-navy flex items-center gap-2">
                <History className="h-5 w-5 text-blue-600" />
                Histórico de Alertas
              </DrawerTitle>
              {/* Botão de som */}
              <button
                onClick={() => setSomAtivado(!somAtivado)}
                className={`h-10 w-10 rounded-full flex items-center justify-center transition-all border ${
                  somAtivado 
                    ? 'bg-green-50 text-green-600 border-green-200' 
                    : 'bg-stone-100 text-stone-400 border-stone-200'
                }`}
                title={somAtivado ? 'Som ativado - clique para desativar' : 'Som desativado - clique para ativar'}
              >
                {somAtivado ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </button>
            </div>
            <DrawerDescription className="text-stone-500">
              Consulte os últimos alertas recebidos
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-4 space-y-3 overflow-y-auto max-h-[60vh]">
            {todosAlertas.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum alerta ainda</p>
              </div>
            ) : (
              todosAlertas.slice(0, 20).map((alerta) => {
                const alertaConfig = PRIORIDADE_CONFIG[alerta.prioridade] || PRIORIDADE_CONFIG.normal;
                const isLido = alerta.lido || alerta.leituras?.includes(userId);
                return (
                  <div
                    key={alerta.id}
                    className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                      isLido ? 'bg-stone-50 border-stone-200 opacity-70' : `${alertaConfig.bgColor} ${alertaConfig.borderColor}`
                    }`}
                    onClick={() => {
                      setShowHistorico(false);
                      handleVerAlerta(alerta);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${isLido ? 'bg-stone-200' : 'bg-white/80'}`}>
                        {React.createElement(alertaConfig.icon, { 
                          className: `h-4 w-4 ${isLido ? 'text-stone-400' : alertaConfig.iconColor}` 
                        })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className={`font-semibold truncate ${isLido ? 'text-stone-500' : alertaConfig.textColor}`}>
                            {alerta.titulo}
                          </p>
                          {!isLido && (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">Novo</span>
                          )}
                        </div>
                        <p className="text-sm text-stone-600 line-clamp-2">{alerta.mensagem}</p>
                        {alerta.dados_of?.of_numero && (
                          <p className="text-xs text-stone-500 mt-1">
                            OF: {alerta.dados_of.of_numero} {alerta.dados_of.marca && `• ${alerta.dados_of.marca}`}
                          </p>
                        )}
                        <p className="text-xs text-stone-400 mt-1">
                          {new Date(alerta.data_notificacao || alerta.created_at).toLocaleString('pt-PT', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
